import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import { FORM_FIELDS } from '@/pages/getRecipe/microLayering/materialAdvice/utils/formTypes'

import { PLACEMENT } from './appSlice'
import { CrownInput, StumpShadeInput } from './stainingStudio'

interface StumpShadeStainingRecipe {
  [PLACEMENT.LEFT]: {
    [key: string]: {
      input: CrownInput
      output: any
    }
  }
  [PLACEMENT.RIGHT]: {
    [key: string]: {
      input: CrownInput
      output: any
    }
  }
}

interface stainingRecipe {
  [PLACEMENT.LEFT]: {
    [key: string]: {
      input: CrownInput
      output: any
    }
  }
  [PLACEMENT.RIGHT]: {
    [key: string]: {
      input: CrownInput
      output: any
    }
  }
}
export interface microLayeringState {
  materialAdviceActiveStep: number
  materialAdviceForm: { [key in keyof FormData]?: any }
  selectedMaterialPreparationKey: string
  selectedMaterialTargetKey: string
  materialPreparationTargetColor: any
  materialTargetColor: any
  materialAdviceOuput: any
  stumpShadeTargetColor: any
  stumpShadePreparationColor: any
  stumpSahdeStainingBrand: string
  stumpShadeRecipeOutput: any
  selectedStumpShadeBrand: string | null
  selectedStumpShadeKey: string | null
  stumpShadeRecipe: StumpShadeStainingRecipe | null
  internalEffectCeramicBrand: string
  selectedInternalEffect: string
  internalEffects: any
  internalEffectOutput: any
  selectedStainingKey: string | null
  stainingRecipe: stainingRecipe | null
  selectedStainingBrand: string | null
  enamelCeramicBrand: string
  enamelTypesByRegion: Record<string, string>
  measuringRegion: string
  enamelRecipe: any
  enamelPosition: any
  frameworkRecipeType: string
}

const initialState: microLayeringState = {
  materialAdviceActiveStep: 0,
  materialAdviceForm: {
    [FORM_FIELDS.availspace_CERVICAL]: 0.5,
    [FORM_FIELDS.availspace_INCISAL]: 0.5,
    [FORM_FIELDS.availspace_MIDDLE]: 0.5,
    [FORM_FIELDS.INDICATION]: '',
    [FORM_FIELDS.CAPBRAND]: '',
  },
  selectedMaterialPreparationKey: '',
  selectedMaterialTargetKey: '',
  materialPreparationTargetColor: {},
  materialTargetColor: {},
  materialAdviceOuput: {},
  frameworkRecipeType: 'Single Zone',

  //Stump Shade
  stumpSahdeStainingBrand: '',
  stumpShadeTargetColor: {},
  stumpShadePreparationColor: {},
  stumpShadeRecipeOutput: {},

  //Stump Shade Staining
  selectedStumpShadeBrand: null,
  selectedStumpShadeKey: null,
  stumpShadeRecipe: {
    [PLACEMENT.LEFT]: {},
    [PLACEMENT.RIGHT]: {},
  },

  //Internal Effect
  internalEffectCeramicBrand: '',
  selectedInternalEffect: '',
  internalEffects: {},
  internalEffectOutput: {},

  //Staining
  selectedStainingKey: null,
  stainingRecipe: {
    [PLACEMENT.LEFT]: {},
    [PLACEMENT.RIGHT]: {},
  },
  selectedStainingBrand: null,

  //Enamel
  enamelCeramicBrand: '',
  enamelTypesByRegion: {},
  measuringRegion: '',
  enamelRecipe: {},
  enamelPosition: {},
}

export const microLayeringSlice = createSlice({
  name: 'microLayering',
  initialState,
  reducers: {
    setMaterialRecipeActiveStep: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        materialAdviceActiveStep: action.payload,
      }
    },
    updateMaterialAdviceValue: (
      state,
      action: PayloadAction<{ name: keyof FormData; value: any }>,
    ) => {
      return {
        ...state,
        materialAdviceForm: {
          ...state.materialAdviceForm,
          [action.payload.name]: action.payload.value,
        },
      }
    },
    setSelectedMaterialPreparationKey: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.selectedMaterialPreparationKey = action.payload
    },
    setSelectedMaterialTargetKey: (state, action: PayloadAction<string>) => {
      state.selectedMaterialTargetKey = action.payload
    },
    setMaterialPreparationTargetColor: (
      state,
      action: PayloadAction<{
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { targetcolor, position, imagePosition } = action.payload

      state.materialPreparationTargetColor[position] = {
        targetcolor,
        imagePosition,
      }
    },
    setMaterialTargetColor: (
      state,
      action: PayloadAction<{
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { targetcolor, position, imagePosition } = action.payload

      state.materialTargetColor[position] = {
        targetcolor,
        imagePosition,
      }
    },
    setMaterialAdviceOuput: (state, action) => {
      const { imageId, result } = action.payload

      if (!state.materialAdviceOuput) {
        state.materialAdviceOuput = {}
      }
      if (result === undefined) {
        delete state.materialAdviceOuput[imageId]
      } else {
        state.materialAdviceOuput[imageId] = {
          ...result,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },
    resetFrameworkRecipeState: (
      state,
      action: PayloadAction<{ imageId: string }>,
    ) => {
      const { imageId } = action.payload

      delete state.materialAdviceOuput[imageId]
      state.materialPreparationTargetColor = {}
      state.materialTargetColor = {}
      state.materialTargetColor[imageId]
      state.materialAdviceForm = initialState.materialAdviceForm
      state.materialAdviceActiveStep = initialState.materialAdviceActiveStep

      state.selectedMaterialPreparationKey =
        initialState.selectedMaterialPreparationKey
      state.selectedMaterialTargetKey = initialState.selectedMaterialTargetKey
    },
    setFrameworkRecipeType: (state, action) => {
      state.frameworkRecipeType = action.payload
    },

    //Stump Shade
    setStumpShadeStainingBrand: (state, action: PayloadAction<string>) => {
      state.stumpSahdeStainingBrand = action.payload
    },
    setStumpShadePreparationTarget: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { imageId, targetcolor, position, imagePosition } = action.payload

      if (!state.stumpShadePreparationColor[imageId]) {
        state.stumpShadePreparationColor[imageId] = {}
      }

      state.stumpShadePreparationColor[imageId][position] = {
        targetcolor,
        imagePosition,
      }
    },
    setStumpShadeTargetColor: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { imageId, targetcolor, position, imagePosition } = action.payload

      if (!state.stumpShadeTargetColor[imageId]) {
        state.stumpShadeTargetColor[imageId] = {}
      }

      state.stumpShadeTargetColor[imageId][position] = {
        targetcolor,
        imagePosition,
      }
    },
    setStumpShadeRecipeOutput: (state, action) => {
      const { imageId, key, result } = action.payload

      if (!state.stumpShadeRecipeOutput) {
        state.stumpShadeRecipeOutput = {}
      }

      if (!state.stumpShadeRecipeOutput[imageId]) {
        state.stumpShadeRecipeOutput[imageId] = {}
      }

      if (result === undefined) {
        delete state.stumpShadeRecipeOutput[imageId][key]
      } else {
        state.stumpShadeRecipeOutput[imageId][key] = result
      }
    },
    removeStumpShadeTargetColor: (state, action) => {
      const { imageId, key } = action.payload
      delete state.stumpShadeTargetColor?.[imageId]?.[key]
    },
    removeStumpShadePreparationColor: (state, action) => {
      const { imageId, key } = action.payload
      delete state.stumpShadePreparationColor?.[imageId]?.[key]
    },

    //Stump Sahde Staining
    setSelectedStumpShadeStainingBrand: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      return {
        ...state,
        selectedStumpShadeBrand: action.payload,
      }
    },
    setSelectedStumpShadeKey: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        selectedStumpShadeKey: action.payload,
      }
    },
    setStumpShadeRecipe: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: {
          input?: StumpShadeInput
          output?: any
        }
      }>,
    ) => {
      const placement = action.payload
        .placement as unknown as keyof StumpShadeInput
      const currentRecipe = state.stumpShadeRecipe || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }

      return {
        ...state,
        stumpShadeRecipe: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [action.payload.key]: {
              ...currentRecipe[placement][action.payload.key],
              ...action.payload.data,
            },
          },
        },
      }
    },
    removeStumpShadeRecipeKey: (
      state,
      action: PayloadAction<{
        key: string
      }>,
    ) => {
      if (state?.stumpShadeRecipe?.[PLACEMENT.LEFT]) {
        delete state.stumpShadeRecipe[PLACEMENT.LEFT][action.payload.key]
      }
      if (state?.stumpShadeRecipe?.[PLACEMENT.RIGHT]) {
        delete state.stumpShadeRecipe[PLACEMENT.RIGHT][action.payload.key]
      }
    },
    clearMicroLayeringStumpShadeStainingState: () => initialState,

    //Internal Effect
    setInternalEffectCeramicBrand: (state, action: PayloadAction<string>) => {
      state.internalEffectCeramicBrand = action.payload
    },
    setSelectedInternalEffect: (state, action: PayloadAction<string>) => {
      state.selectedInternalEffect = action.payload
    },
    setInternalEffectPosition: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }>,
    ) => {
      const { imageId, position, output } = action.payload
      const key = state.selectedInternalEffect
      if (!key || !imageId) return

      if (!state.internalEffects) {
        state.internalEffects = {}
      }

      if (!state.internalEffects[imageId]) {
        state.internalEffects[imageId] = {}
      }

      if (!state.internalEffects[imageId][key]) {
        state.internalEffects[imageId][key] = {}
      }

      const existing = state.internalEffects[imageId][key]

      state.internalEffects[imageId][key] = {
        ...existing,
        ...(position && { position }),
        ...(output && {
          output: {
            ...existing.output,
            ...output,
          },
        }),
      }
    },
    setInternalEffectRecipeOutput: (state, action) => {
      const { imageId, key, result } = action.payload

      if (!state.internalEffectOutput) {
        state.internalEffectOutput = {}
      }

      if (!state.internalEffectOutput[imageId]) {
        state.internalEffectOutput[imageId] = {}
      }

      if (result === undefined) {
        delete state.internalEffectOutput[imageId][key]
      } else {
        state.internalEffectOutput[imageId][key] = {
          ...result,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },

    //Staining
    setSelectedStainingKey: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        selectedStainingKey: action.payload,
      }
    },
    setSelectedStainingBrand: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        selectedStainingBrand: action.payload,
      }
    },
    setStainingRecipe: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: {
          input?: CrownInput
          output?: any
        }
      }>,
    ) => {
      const placement = action.payload.placement as keyof stainingRecipe
      const currentRecipe = state.stainingRecipe || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }
      const expiresAt = Date.now() + 24 * 60 * 60 * 1000

      return {
        ...state,
        stainingRecipe: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [action.payload.key]: {
              ...currentRecipe[placement][action.payload.key],
              ...action.payload.data,
              expiresAt,
            },
          },
        },
      }
    },
    clearMLStainingState: () => initialState,
    removeStainingKey: (
      state,
      action: PayloadAction<{
        key: string
      }>,
    ) => {
      if (state?.stainingRecipe?.[PLACEMENT.LEFT]) {
        delete state.stainingRecipe[PLACEMENT.LEFT][action.payload.key]
      }
      if (state?.stainingRecipe?.[PLACEMENT.RIGHT]) {
        delete state.stainingRecipe[PLACEMENT.RIGHT][action.payload.key]
      }
    },

    //Enamel
    setEnamelCeramicBrand: (state, action: PayloadAction<string>) => {
      state.enamelCeramicBrand = action.payload
    },
    setEnamelTypeByRegion(
      state,
      action: PayloadAction<{ region: string; value: string }>,
    ) {
      if (action.payload.value === '') {
        delete state.enamelTypesByRegion[action.payload.region]
      } else {
        state.enamelTypesByRegion[action.payload.region] = action.payload.value
      }
    },
    setMeasuringRegion: (state, action) => {
      state.measuringRegion = action.payload
    },
    setEnamelRecipeOutput: (state, action) => {
      const { imageId, key, result } = action.payload

      if (!state.enamelRecipe) {
        state.enamelRecipe = {}
      }

      if (!state.enamelRecipe[imageId]) {
        state.enamelRecipe[imageId] = {}
      }

      if (result === undefined) {
        delete state.enamelRecipe[imageId][key]
      } else {
        state.enamelRecipe[imageId][key] = {
          ...result,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },
    setEnamelPosition: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }>,
    ) => {
      const { imageId, position, output } = action.payload
      const key = state.measuringRegion
      if (!key || !imageId) return

      if (!state.enamelPosition) {
        state.enamelPosition = {}
      }

      if (!state.enamelPosition[imageId]) {
        state.enamelPosition[imageId] = {}
      }

      if (!state.enamelPosition[imageId][key]) {
        state.enamelPosition[imageId][key] = {}
      }

      const existing = state.enamelPosition[imageId][key]

      state.enamelPosition[imageId][key] = {
        ...existing,
        ...(position && { position }),
        ...(output && {
          output: {
            ...existing.output,
            ...output,
          },
        }),
      }
    },
  },
})

export const {
  setMaterialRecipeActiveStep,
  updateMaterialAdviceValue,
  setSelectedMaterialPreparationKey,
  setSelectedMaterialTargetKey,
  setMaterialPreparationTargetColor,
  setMaterialTargetColor,
  setMaterialAdviceOuput,
  resetFrameworkRecipeState,
  setFrameworkRecipeType,

  //Stump shade
  setStumpShadePreparationTarget,
  setStumpShadeTargetColor,
  setStumpShadeStainingBrand,
  setStumpShadeRecipeOutput,
  removeStumpShadeTargetColor,
  removeStumpShadePreparationColor,

  //Stump Shade Staining
  setSelectedStumpShadeStainingBrand,
  setSelectedStumpShadeKey,
  setStumpShadeRecipe,
  removeStumpShadeRecipeKey,
  clearMicroLayeringStumpShadeStainingState,

  //Internal Effect
  setInternalEffectCeramicBrand,
  setSelectedInternalEffect,
  setInternalEffectPosition,
  setInternalEffectRecipeOutput,

  //Staining
  setSelectedStainingKey,
  setSelectedStainingBrand,
  setStainingRecipe,
  clearMLStainingState,
  removeStainingKey,

  //Enamel
  setEnamelCeramicBrand,
  setEnamelTypeByRegion,
  setMeasuringRegion,
  setEnamelRecipeOutput,
  setEnamelPosition,
} = microLayeringSlice.actions
export default microLayeringSlice.reducer
